import React, { createContext, useEffect, useState, useCallback } from "react";
import { useValidToken } from "../../hooks/useValidToken";
import useAuthUser from "../../hooks/useAuthUser";

// Listado original (oportunidades)
import getCodingOpportunities from "../../utils/services/CodingOpportunities/getCodingOpportunities";

// Nuevo listado (pacientes agrupados con conteo)
import getCodingOpportunitiesPatients from "../../utils/services/CodingOpportunities/getCodingOpportunitiesPatients";

// Actualizar estado del paciente
import updatePatientStatusService from "../../utils/services/CodingOpportunities/updatePatientStatus";

// Actualizar estado del coding opportunity individual
import updateCodingOpportunityStatusService from "../../utils/services/CodingOpportunities/updateCodingOpportunityStatus";

// Crear comentario en coding opportunity
import createCodingOpCommentService from "../../utils/services/CodingOpportunities/createCodingOpComment";

// Crear comentario a nivel de paciente
import createPatientCommentService from "../../utils/services/CodingOpportunities/createPatientComment";

// LocalStorage utilities for patient status and overrides
import {
  getAllPatientStatuses,
  getAllOverrides,
} from "../../modules/coding-opportunities/utils/codingOpStorage";

export const CodingOpContext = createContext();

export const CodingOpProvider = ({ children }) => {
  const { isValid, authLoading } = useValidToken();
  const user = useAuthUser();
  const employeeId = user?.employeeId || user?.employee || null;

  // ===================== Helpers =====================

  const canCallApi = useCallback(() => !authLoading && isValid, [authLoading, isValid]);

  const getCurrentUserName = () =>
    localStorage.getItem("username") ||
    localStorage.getItem("userEmail") ||
    localStorage.getItem("user") ||
    "";

  // ===================== Estado general =====================
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // ===================== Filtros del listado (OPPORTUNITIES) =====================
  // ⚠️ En v3 NO se auto-consume este listado global; se consume por paciente con loadCodingOpportunitiesByPatient()
  const [filters, setFilters] = useState({
    pcpId: "",
    status: "",
    measureYear: "",
    memberType: "",
    cipId: "",
    locationId: "",
    page: 1,
    pageSize: 50,
  });

  // ===================== Estado de listado (OPPORTUNITIES) =====================
  const [codingOps, setCodingOps] = useState([]);
  const [totalCount, setTotalCount] = useState(0);

  // ===================== Filtros del listado (PACIENTES) =====================
  const [patientsFilters, setPatientsFilters] = useState({
    pcpId: "",
    measureYear: "",
    memberType: "",
    cipId: "",
    patientName: "",
    dob: "",
    patientStatus: "",
    patientWorkflowStatus: "",
    locationId: "",
    networkName: "",
    memberPlan: "",
    page: 1,
    pageSize: 15,
  });

  // ===================== Estado de listado (PACIENTES) =====================
  const [patients, setPatients] = useState([]);
  const [patientsTotalCount, setPatientsTotalCount] = useState(0);

  // ===================== Detalle / Chat =====================
  const [selectedCodingOpId, setSelectedCodingOpId] = useState(null);

  // messageMap: { [codingOpportunityId]: [ { id, comment, authorName, authorRole, createdAt } ] }
  const [messageMap, setMessageMap] = useState({});

  const messages = selectedCodingOpId ? messageMap[selectedCodingOpId] || [] : [];

  // =======================================================================
  //                         LISTADO (PACIENTES) - WORKLIST V3
  // =======================================================================

  const loadCodingOpportunitiesPatients = useCallback(
    async (overrideFilters = {}) => {
      if (!canCallApi()) return;

      const params = { ...patientsFilters, ...overrideFilters };

      setLoading(true);
      setError(null);

      try {
        const result = await getCodingOpportunitiesPatients(params);
        const rawItems = Array.isArray(result?.data) ? result.data : [];

        // Get local overrides and statuses
        const localStatuses = getAllPatientStatuses();
        const localOverrides = getAllOverrides();

        const items = rawItems.map((row) => {
          const cipId = row.cipId;
          const localStatus = localStatuses[cipId];
          const localOverride = localOverrides[cipId];

          return {
            id: cipId,
            cipId: cipId,
            memberFullName: row.memberFullName,
            dateOfBirth: row.dateOfBirth,
            gender: row.gender,
            networkName: row.networkName,
            metalLevel: row.metalLevel,
            memberPlan: row.memberPlan,
            patientStatus: row.patientStatus,
            // Use local status if available, otherwise use API value
            patientWorkflowStatus: localStatus?.status || row.patientWorkflowStatus,
            // Store the local status details for display
            localPatientStatus: localStatus || null,
            // Count of local overrides for this patient
            localOverridesCount: localOverride ? Object.keys(localOverride).length : 0,
            pcpId: row.pcpId,
            pcpName: row.pcpName,
            locationId: row.locationId,
            medicalCenter: row.medicalCenter,
            codingOpCount: row.codingOpCount,
          };
        });

        setPatients(items);

        setPatientsTotalCount(
          typeof result?.total === "number" ? result.total : items.length
        );
      } catch (err) {
        console.error("Error loading coding opportunities patients:", err);
        setError(err);
        setPatients([]);
        setPatientsTotalCount(0);
      } finally {
        setLoading(false);
      }
    },
    [canCallApi, patientsFilters]
  );

  // ✅ Auto-carga worklist (pacientes) cuando cambian filtros o token
  useEffect(() => {
    if (!canCallApi()) return;
    loadCodingOpportunitiesPatients();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, isValid, patientsFilters]);

  const resetPatientsFilters = () => {
    setPatientsFilters({
      pcpId: "",
      measureYear: "",
      memberType: "",
      cipId: "",
      patientName: "",
      dob: "",
      patientStatus: "",
      patientWorkflowStatus: "",
      locationId: "",
      networkName: "",
      memberPlan: "",
      page: 1,
      pageSize: 15,
    });
  };

  const setPatientsPage = (page) => {
    setPatientsFilters((prev) => ({ ...prev, page }));
  };

  const setPatientsPageSize = (pageSize) => {
    setPatientsFilters((prev) => ({ ...prev, pageSize, page: 1 }));
  };

  // Force refresh patients (useful after local changes)
  const refreshPatients = useCallback(() => {
    loadCodingOpportunitiesPatients();
  }, [loadCodingOpportunitiesPatients]);

  // =======================================================================
  //                         LISTADO (OPPORTUNITIES) - SOLO ON DEMAND
  // =======================================================================

  // Mantengo este método por compatibilidad, pero en v3 lo ideal es usar loadCodingOpportunitiesByPatient.
  const loadCodingOpportunities = useCallback(
    async (overrideFilters = {}) => {
      if (!canCallApi()) return;

      const params = { ...filters, ...overrideFilters };

      setLoading(true);
      setError(null);

      try {
        const result = await getCodingOpportunities(params);
        const rawItems = Array.isArray(result?.data) ? result.data : [];

        const items = rawItems.map((row) => ({
          id: row.codingOpportunityId,
          codingOpportunityId: row.codingOpportunityId,

          cipId: row.cipId,
          memberId: row.memberId,
          memberFullName: row.memberFullName,
          dateOfBirth: row.dateOfBirth,
          gender: row.gender,
          email: row.email,
          phoneNumber: row.phoneNumber,
          mobileNumber: row.mobileNumber,

          hccId: row.hccId,
          hccDescription: row.hccDescription,
          diagnosisCode: row.diagnosisCode,
          diagnosisDescription: row.diagnosisDescription,

          pcpId: row.pcpId,
          pcpName: row.pcpName,
          locationId: row.locationId,
          medicalCenter: row.medicalCenter,

          measureYear: row.measureYear,
          initialDos: row.initialDos,
          lastDos: row.lastDos,

          status: row.status,
          statusName: row.statusName,

          memberType: row.memberType,
        }));

        setCodingOps(items);
        setTotalCount(typeof result?.total === "number" ? result.total : items.length);
      } catch (err) {
        console.error("Error loading coding opportunities:", err);
        setError(err);
        setCodingOps([]);
        setTotalCount(0);
      } finally {
        setLoading(false);
      }
    },
    [canCallApi, filters]
  );

  // ✅ Este es el que usaremos en el detalle del paciente (v3)
  const loadCodingOpportunitiesByPatient = useCallback(
    async (cipId, override = {}) => {
      if (!canCallApi()) return;
      if (!cipId) return;

      const params = {
        ...filters,
        ...override,
        cipId,
        page: 1,
        pageSize: override?.pageSize || 200, // detalle: trae todo
      };

      setLoading(true);
      setError(null);

      try {
        const result = await getCodingOpportunities(params);
        const rawItems = Array.isArray(result?.data) ? result.data : [];

        const items = rawItems.map((row) => ({
          id: row.codingOpportunityId,
          codingOpportunityId: row.codingOpportunityId,

          cipId: row.cipId,
          memberId: row.memberId,
          memberFullName: row.memberFullName,
          dateOfBirth: row.dateOfBirth,
          gender: row.gender,

          hccId: row.hccId,
          hccDescription: row.hccDescription,
          diagnosisCode: row.diagnosisCode,
          diagnosisDescription: row.diagnosisDescription,

          pcpId: row.pcpId,
          pcpName: row.pcpName,
          locationId: row.locationId,
          medicalCenter: row.medicalCenter,

          measureYear: row.measureYear,
          initialDos: row.initialDos,
          lastDos: row.lastDos,

          status: row.status,
          statusName: row.statusName,

          memberType: row.memberType,
        }));

        setCodingOps(items);
        setTotalCount(typeof result?.total === "number" ? result.total : items.length);
      } catch (err) {
        console.error("Error loading coding opportunities by patient:", err);
        setError(err);
        setCodingOps([]);
        setTotalCount(0);
      } finally {
        setLoading(false);
      }
    },
    [canCallApi, filters]
  );

  const resetFilters = () => {
    setFilters({
      pcpId: "",
      status: "",
      measureYear: "",
      memberType: "",
      cipId: "",
      locationId: "",
      page: 1,
      pageSize: 50,
    });
  };

  const setPage = (page) => {
    setFilters((prev) => ({ ...prev, page }));
  };

  const setPageSize = (pageSize) => {
    setFilters((prev) => ({ ...prev, pageSize, page: 1 }));
  };

  // =======================================================================
  //                         DETALLE / SELECCIÓN
  // =======================================================================

  const selectCodingOpportunity = (codingOpportunityId) => {
    setSelectedCodingOpId(codingOpportunityId || null);
  };

  // =======================================================================
  //                              CHAT (in-memory)
  // =======================================================================

  const addMessage = ({ codingOpportunityId, text, authorRole }) => {
    const targetId = codingOpportunityId || selectedCodingOpId;
    if (!targetId || !text) return;

    const message = {
      id: Date.now(),
      codingOpportunityId: targetId,
      comment: text,
      authorName: getCurrentUserName() || "User",
      authorRole: authorRole || "CODER",
      createdAt: new Date().toISOString(),
    };

    setMessageMap((prev) => {
      const prevList = prev[targetId] || [];
      return {
        ...prev,
        [targetId]: [...prevList, message],
      };
    });
  };

  // =======================================================================
  //                         UPDATE PATIENT STATUS
  // =======================================================================

  /**
   * Actualiza el estado del paciente en el backend
   * @param {string} cipId - ID del paciente (CIP)
   * @param {number} patStatus - ID del nuevo estado (0-7)
   * @returns {Promise<boolean>} - true si fue exitoso
   */
  const updatePatientStatus = async (cipId, patStatus) => {
    if (!canCallApi()) return false;
    if (!cipId || patStatus === undefined || patStatus === null) return false;

    setError(null);

    try {
      await updatePatientStatusService({
        cipId,
        patStatus,
        employeeUpdate: employeeId,
      });

      // Refresh patients list to reflect the change
      await loadCodingOpportunitiesPatients();

      return true;
    } catch (err) {
      console.error("Error updating patient status:", err);
      setError(err);
      return false;
    }
  };

  // =======================================================================
  //                    UPDATE CODING OPPORTUNITY STATUS
  // =======================================================================

  /**
   * Actualiza el estado de un coding opportunity individual
   * @param {number} codingOpportunityId - ID del coding opportunity
   * @param {number} codStatus - ID del nuevo estado (0, 51-59)
   * @returns {Promise<Object|null>} - { message, rowsAffected } o null si error
   */
  const updateCodingOpportunityStatus = async (codingOpportunityId, codStatus) => {
    if (!canCallApi()) return null;
    if (!codingOpportunityId || codStatus === undefined || codStatus === null) return null;

    setError(null);

    try {
      const result = await updateCodingOpportunityStatusService({
        codingOpportunityId,
        codStatus,
        employeeUpdate: employeeId,
        updateDate: new Date().toISOString(),
      });

      return result;
    } catch (err) {
      console.error("Error updating coding opportunity status:", err);
      setError(err);
      return null;
    }
  };

  /**
   * Aprueba un coding opportunity (shortcut para estado APPROVE = 58)
   * @param {number} codingOpportunityId - ID del coding opportunity
   * @returns {Promise<Object|null>} - { message, rowsAffected } o null si error
   */
  const approveCodingOpportunity = async (codingOpportunityId) => {
    return updateCodingOpportunityStatus(codingOpportunityId, 58); // APPROVE
  };

  // =======================================================================
  //                    ADD CODING OPPORTUNITY COMMENT
  // =======================================================================

  /**
   * Agrega un comentario a un coding opportunity
   * @param {number} codingOpportunityId - ID del coding opportunity
   * @param {string} comment - Texto del comentario
   * @param {string} [authorRole] - Rol del autor (default: user.rol o "USER")
   * @returns {Promise<Object|null>} - { message, commentId } o null si error
   */
  const addCodingOpComment = async (codingOpportunityId, comment, authorRole) => {
    if (!canCallApi()) return null;
    if (!codingOpportunityId || !comment || !comment.trim()) return null;

    setError(null);

    try {
      const result = await createCodingOpCommentService({
        codingOpportunityId,
        employeeId: employeeId,
        authorRole: authorRole || user?.rol || "USER",
        comment: comment.trim(),
      });

      return result;
    } catch (err) {
      console.error("Error adding coding opportunity comment:", err);
      setError(err);
      return null;
    }
  };

  // =======================================================================
  //                    ADD PATIENT COMMENT
  // =======================================================================

  /**
   * Agrega un comentario a nivel de paciente en Coding Opportunities
   * @param {string} cipId - ID del paciente (CIP)
   * @param {string} comment - Texto del comentario
   * @param {string} [authorRole] - Rol del autor (default: user.rol o "USER")
   * @returns {Promise<Object|null>} - { message, commentId } o null si error
   */
  const addPatientComment = async (cipId, comment, authorRole) => {
    if (!canCallApi()) return null;
    if (!cipId || !comment || !comment.trim()) return null;

    setError(null);

    try {
      const result = await createPatientCommentService({
        cipId,
        employeeId: employeeId,
        authorRole: authorRole || user?.rol || "USER",
        comment: comment.trim(),
      });

      return result;
    } catch (err) {
      console.error("Error adding patient comment:", err);
      setError(err);
      return null;
    }
  };

  // =======================================================================
  //                               PROVIDER
  // =======================================================================

  return (
    <CodingOpContext.Provider
      value={{
        // patients list (worklist)
        patients,
        patientsTotalCount,
        patientsFilters,
        setPatientsFilters,
        resetPatientsFilters,
        setPatientsPage,
        setPatientsPageSize,
        loadCodingOpportunitiesPatients,
        refreshPatients,

        // opportunities (detalle / soporte)
        codingOps,
        totalCount,
        filters,
        setFilters,
        resetFilters,
        setPage,
        setPageSize,
        loadCodingOpportunities,
        loadCodingOpportunitiesByPatient,

        // selección / detalle
        selectedCodingOpId,
        selectCodingOpportunity,

        // chat
        messages,
        addMessage,

        // patient status
        updatePatientStatus,

        // coding opportunity status
        updateCodingOpportunityStatus,
        approveCodingOpportunity,

        // coding opportunity comments
        addCodingOpComment,

        // patient comments
        addPatientComment,

        // estado global
        loading,
        error,
      }}
    >
      {children}
    </CodingOpContext.Provider>
  );
};
